import React, { useState, useEffect } from "react";
import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import styles from "./FormsSuccess.module.css";

interface LocationState {
  formData: Record<string, any>;
  formTitle: string;
  submissionToken?: string;
  fieldLabels?: Record<string, string>;
  successTitle?: string;
  successText?: string;
}

const FormsSuccess: React.FC = () => {
  const { formId, submissionId } = useParams<{
    formId: string;
    submissionId: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<Record<string, any> | null>(null);
  const [formTitle, setFormTitle] = useState<string>("");
  const [fieldLabels, setFieldLabels] = useState<Record<string, string>>({});
  const [successTitle, setSuccessTitle] = useState<string>("Thank You!");
  const [successText, setSuccessText] = useState<string>(
    "Your submission has been received successfully."
  );
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    // Check if the user is authorized to view this page
    const checkAuthorization = () => {
      setIsLoading(true);

      // Try to get state from location first
      const state = location.state as LocationState | undefined;

      // Check if user came directly from form submission
      if (state?.formData) {
        // Generate a one-time token and store it in sessionStorage
        const submissionToken =
          state.submissionToken ||
          `token-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
        sessionStorage.setItem(
          `form_submission_token_${submissionId}`,
          submissionToken
        );

        // Store form data with the token for verification
        localStorage.setItem(
          `form_submission_${submissionId}`,
          JSON.stringify({
            formData: state.formData,
            formTitle: state.formTitle,
            fieldLabels: state.fieldLabels || {},
            successTitle: state.successTitle,
            successText: state.successText,
            submissionToken,
            accessedAt: Date.now(),
          })
        );

        setFormData(state.formData);
        setFormTitle(state.formTitle || "Form");
        setFieldLabels(state.fieldLabels || {});

        // Use custom success messages if available
        if (state.successTitle) setSuccessTitle(state.successTitle);
        if (state.successText) setSuccessText(state.successText);

        setIsAuthorized(true);
        setIsLoading(false);
        return;
      }

      // If no state, check sessionStorage for a valid token
      const savedToken = sessionStorage.getItem(
        `form_submission_token_${submissionId}`
      );
      const savedData = localStorage.getItem(`form_submission_${submissionId}`);

      if (!savedData || !savedToken) {
        // No saved data or token, unauthorized access
        setIsAuthorized(false);
        setIsLoading(false);
        return;
      }

      try {
        const parsedData = JSON.parse(savedData);

        // Check if token matches and access time is recent (within 30 minutes)
        if (
          parsedData.submissionToken === savedToken &&
          parsedData.accessedAt &&
          Date.now() - parsedData.accessedAt < 30 * 60 * 1000
        ) {
          setFormData(parsedData.formData);
          setFormTitle(parsedData.formTitle || "Form");
          setFieldLabels(parsedData.fieldLabels || {});

          // Use custom success messages if available from saved data
          if (parsedData.successTitle) setSuccessTitle(parsedData.successTitle);
          if (parsedData.successText) setSuccessText(parsedData.successText);

          setIsAuthorized(true);
        } else {
          // Token expired or doesn't match
          setIsAuthorized(false);
        }
      } catch (e) {
        console.error("Failed to parse saved form data", e);
        setIsAuthorized(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthorization();
  }, [location.state, submissionId, navigate]);

  // Format value for display
  const formatValue = (value: any): string => {
    if (value === null || value === undefined) {
      return "Not provided";
    }

    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    if (typeof value === "object") {
      return JSON.stringify(value);
    }

    return String(value);
  };

  // Get field label for a given field key
  const getFieldLabel = (key: string): string => {
    return fieldLabels[key] || key;
  };

  if (isLoading) {
    return <div className={styles.loading}>Cargando</div>;
  }

  if (!isAuthorized) {
    return (
      <div className={styles.unauthorizedContainer}>
        <div className={styles.unauthorizedIcon}>
          <svg
            width="64"
            height="64"
            viewBox="0 0 64 64"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="32" cy="32" r="32" fill="#E84747" fillOpacity="0.2" />
            <path
              d="M32 22V36"
              stroke="#E84747"
              strokeWidth="4"
              strokeLinecap="round"
            />
            <circle cx="32" cy="44" r="3" fill="#E84747" />
          </svg>
        </div>
        <h1 className={styles.unauthorizedTitle}>Acceso denegado</h1>
        <p className={styles.unauthorizedMessage}>
          No tienes permiso para ver esta presentación del formulario.
        </p>
        <div className={styles.actions}>
          <Link to="/" className="buttonMain">
            Volver a inicio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.successContainer}>
      <div className={styles.successHeader}>
        <div className={styles.successIcon}>
          <img
            src="https://media.evefant.com/green_check_icon_evf_43f85b4028.svg"
            alt="Success Icon"
            width="64"
            height="64"
          />
        </div>
        <h1 className={styles.successTitle}>{successTitle}</h1>
        <p className={styles.successMessage}>{successText}</p>
        {formTitle && (
          <p className={styles.formName}>
            Formulario: <strong>{formTitle}</strong>
          </p>
        )}
        <p className={styles.submissionId}>
          Id de solicitud: <strong>{submissionId}</strong>
        </p>
      </div>

      {formData && (
        <div className={styles.submissionDetails}>
          <h2 className={styles.detailsTitle}>Detalles de la solicitud</h2>
          <div className={styles.detailsGrid}>
            {Object.entries(formData).map(([key, value]) => (
              <div key={key} className={styles.detailItem}>
                <div className={styles.detailLabel}>{getFieldLabel(key)}</div>
                <div className={styles.detailValue}>{formatValue(value)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={styles.actions}>
        <Link to="/" className="buttonMain">
          Volver a inicio
        </Link>
      </div>
    </div>
  );
};

export default FormsSuccess;
